<template>
  <div class="sync-state-page">
    <div class="sync-state-panel bg-white shadow-sm rounded">
      <div class="d-flex flex-wrap justify-content-between align-items-center mb-3">
        <div>
          <h4 class="mb-1">
            État de synchronisation
          </h4>
          <small class="text-muted">
            Colis assignés à une société externe mais sans code externe.
          </small>
        </div>
        <div class="d-flex align-items-center mt-2 mt-md-0">
          <b-button
            variant="outline-danger"
            class="mr-2 bg-white"
            :disabled="syncLoading || selectedOrderIds.length === 0 || retryLoading"
            @click="resynchronizeSelected"
          >
            <feather-icon
              icon="RefreshCwIcon"
              size="15"
              class="mr-1"
            />
            Resynchroniser ({{ selectedOrderIds.length }})
          </b-button>
          <b-button
            variant="outline-secondary"
            class="bg-white"
            :disabled="syncLoading"
            @click="fetchFailedSyncOrders"
          >
            <feather-icon
              icon="RefreshCwIcon"
              size="15"
              class="mr-1"
            />
            Rafraîchir
          </b-button>
        </div>
      </div>

      <div class="sync-summary d-flex flex-wrap mb-3">
        <div class="sync-metric mr-3 mb-2">
          <span class="text-muted d-block">À traiter</span>
          <strong>{{ totalOrders || 0 }}</strong>
        </div>
        <div class="sync-metric mr-3 mb-2">
          <span class="text-muted d-block">Sélectionnés</span>
          <strong>{{ selectedOrderIds.length }}</strong>
        </div>
        <div class="sync-metric mb-2">
          <span class="text-muted d-block">Page</span>
          <strong>{{ currentPage }}</strong>
        </div>
      </div>

      <div class="d-flex flex-wrap align-items-start mb-3">
        <b-form-input
          v-model="filters.search"
          type="search"
          placeholder="Rechercher BL, client, code..."
          class="mr-2 mb-2"
          style="max-width: 300px"
          @keyup.enter="applyFilters"
        />
        <v-select
          v-model="filters.company"
          :options="outsourcingCompanies"
          :get-option-label="company => company.display_name || company.name"
          :clearable="true"
          placeholder="Société externe"
          class="sync-filter-select mr-2 mb-2"
          @input="applyFilters"
        />
        <v-select
          v-model="filters.supplier"
          :options="suppliers"
          label="username"
          :clearable="true"
          placeholder="Fournisseur"
          class="sync-filter-select mr-2 mb-2"
          @input="applyFilters"
        />
        <v-select
          v-model="filters.governorate"
          :options="governorates"
          label="name"
          :clearable="true"
          placeholder="Gouvernorat"
          class="sync-filter-select mr-2 mb-2"
          @input="applyFilters"
        />
        <b-button
          variant="outline-secondary"
          class="mb-2 bg-white"
          @click="resetFilters"
        >
          Réinitialiser
        </b-button>
      </div>

      <b-overlay
        :show="syncLoading"
        rounded
        opacity="0.65"
      >
        <b-table
          :items="orders"
          :fields="tableFields"
          responsive
          hover
          show-empty
          empty-text="Aucun colis en échec de synchronisation."
          class="sync-table mb-0"
        >
          <template #head(selected)>
            <b-form-checkbox
              :checked="allCurrentOrdersSelected"
              :disabled="!orders.length"
              @change="toggleAllOrders"
            />
          </template>
          <template #cell(selected)="row">
            <b-form-checkbox
              :checked="selectedOrderIds.includes(row.item.id)"
              @change="toggleOrder(row.item.id)"
            />
          </template>
          <template #cell(order)="row">
            <router-link :to="{ name: 'order-show', params: { id: row.item.id } }">
              <strong>{{ row.item.display_cross_tenant_barcode && row.item.cross_tenant_barcode ? row.item.cross_tenant_barcode : row.item.id }}</strong>
            </router-link>
            <small class="text-muted d-block">{{ row.item.barcode || '-' }}</small>
          </template>
          <template #cell(customer)="row">
            <span>{{ row.item.name || '-' }}</span>
            <small class="text-muted d-block">{{ row.item.phone1 || '-' }}</small>
          </template>
          <template #cell(supplier)="row">
            {{ row.item.supplier && row.item.supplier.username ? row.item.supplier.username : '-' }}
          </template>
          <template #cell(outsourcing_company)="row">
            {{ row.item.outsourcing_company || '-' }}
          </template>
          <template #cell(governorate)="row">
            {{ row.item.governorate || '-' }}
            <small class="text-muted d-block">{{ row.item.delegation || '-' }}</small>
          </template>
          <template #cell(created_at)="row">
            {{ formatDateTime(row.item.created_at) }}
          </template>
          <template #cell(sync_state)>
            <b-badge
              variant="light-danger"
              pill
            >
              <feather-icon
                icon="AlertTriangleIcon"
                size="13"
                class="mr-25"
              />
              Non synchronisé
            </b-badge>
          </template>
          <template #cell(actions)="row">
            <b-button
              variant="outline-danger"
              size="sm"
              class="bg-white retry-btn"
              :disabled="isOrderRetrying(row.item)"
              @click="resynchronizeOrders([row.item.id])"
            >
              <b-spinner
                v-if="isOrderRetrying(row.item)"
                small
                class="mr-50"
              />
              <feather-icon
                v-else
                icon="RefreshCwIcon"
                size="14"
                class="mr-50"
              />
              Relancer
            </b-button>
          </template>
        </b-table>
      </b-overlay>

      <div class="d-flex justify-content-between align-items-center mt-3">
        <small class="text-muted">
          {{ totalOrders ? firstVisibleOrder : 0 }} - {{ lastVisibleOrder }} sur {{ totalOrders }}
        </small>
        <div>
          <b-button
            size="sm"
            variant="outline-secondary"
            class="mr-2 bg-white"
            :disabled="currentPage <= 1 || syncLoading"
            @click="previousPage"
          >
            Précédent
          </b-button>
          <b-button
            size="sm"
            variant="outline-secondary"
            class="bg-white"
            :disabled="currentPage >= totalPages || syncLoading"
            @click="nextPage"
          >
            Suivant
          </b-button>
        </div>
      </div>
    </div>

    <b-toast
      v-if="alertVisible"
      :variant="alertVariant"
      :auto-hide-delay="4000"
      solid
      toaster="b-toaster-top-right"
      @hidden="alertVisible = false"
    >
      {{ alertMessage }}
    </b-toast>
  </div>
</template>

<script>
import {
  BBadge, BButton, BFormCheckbox, BFormInput, BOverlay, BSpinner, BTable, BToast,
} from 'bootstrap-vue'
import axios from 'axios'
import vSelect from 'vue-select'

export default {
  name: 'SyncStateOrders',
  components: {
    BBadge,
    BButton,
    BFormCheckbox,
    BFormInput,
    BOverlay,
    BSpinner,
    BTable,
    BToast,
    vSelect,
  },
  data() {
    return {
      orders: [],
      selectedOrderIds: [],
      retryingOrderIds: [],
      syncLoading: false,
      retryLoading: false,
      pollTimer: null,
      pollCount: 0,
      currentPage: 1,
      pageSize: 50,
      totalOrders: 0,
      outsourcingCompanies: [],
      suppliers: [],
      governorates: [],
      filters: {
        search: '',
        company: null,
        supplier: null,
        governorate: null,
      },
      alertVisible: false,
      alertVariant: '',
      alertMessage: '',
      tableFields: [
        { key: 'selected', label: '', class: 'sync-select-col' },
        { key: 'order', label: 'Colis' },
        { key: 'customer', label: 'Client' },
        { key: 'supplier', label: 'Fournisseur' },
        { key: 'outsourcing_company', label: 'Société externe' },
        { key: 'governorate', label: 'Zone' },
        { key: 'created_at', label: 'Date création' },
        { key: 'sync_state', label: 'État' },
        { key: 'actions', label: 'Actions', class: 'text-right' },
      ],
    }
  },
  computed: {
    totalPages() {
      return Math.max(Math.ceil(this.totalOrders / this.pageSize), 1)
    },
    firstVisibleOrder() {
      return ((this.currentPage - 1) * this.pageSize) + 1
    },
    lastVisibleOrder() {
      return Math.min(this.currentPage * this.pageSize, this.totalOrders)
    },
    allCurrentOrdersSelected() {
      return this.orders.length > 0
        && this.orders.every(order => this.selectedOrderIds.includes(order.id))
    },
  },
  created() {
    this.fetchInitialData()
  },
  beforeDestroy() {
    this.stopPolling()
  },
  methods: {
    async fetchInitialData() {
      await Promise.all([
        this.fetchOutsourcingCompanies(),
        this.fetchSuppliers(),
        this.fetchGovernorates(),
      ])
      await this.fetchFailedSyncOrders()
    },
    async fetchOutsourcingCompanies() {
      try {
        const { data } = await axios.get('/api/companies/outsourcing-company/')
        this.outsourcingCompanies = data.results || data || []
      } catch (error) {
        this.showAlert('danger', 'Erreur lors du chargement des sociétés externes')
      }
    },
    async fetchSuppliers() {
      try {
        const { data } = await axios.get('/api/auth/users/suppliers/')
        this.suppliers = data.results || data || []
      } catch (error) {
        this.showAlert('danger', 'Erreur lors du chargement des fournisseurs')
      }
    },
    async fetchGovernorates() {
      try {
        const { data } = await axios.get('/api/governorates/')
        this.governorates = data.results || data || []
      } catch (error) {
        this.showAlert('danger', 'Erreur lors du chargement des gouvernorats')
      }
    },
    async fetchFailedSyncOrders() {
      this.syncLoading = true
      try {
        const params = {
          page: this.currentPage,
          page_size: this.pageSize,
          is_synchronized: false,
        }
        if (this.filters.search) params.search = this.filters.search
        if (this.filters.company) params.outsourcing_company = this.filters.company.id
        if (this.filters.supplier) params.supplier = this.filters.supplier.id
        if (this.filters.governorate) params.governorate = this.filters.governorate.id

        const { data } = await axios.get('/api/orders/list/', { params })
        this.orders = data.results || []
        this.totalOrders = data.count || 0
        this.selectedOrderIds = this.selectedOrderIds.filter(id => this.orders.some(order => order.id === id))
      } catch (error) {
        this.showAlert('danger', 'Erreur lors du chargement des échecs de synchronisation')
      } finally {
        this.syncLoading = false
      }
    },
    formatDateTime(value) {
      if (!value) return '-'
      return new Date(value).toLocaleString('fr-FR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    applyFilters() {
      this.currentPage = 1
      this.selectedOrderIds = []
      this.fetchFailedSyncOrders()
    },
    resetFilters() {
      this.filters = {
        search: '',
        company: null,
        supplier: null,
        governorate: null,
      }
      this.applyFilters()
    },
    toggleOrder(orderId) {
      if (this.selectedOrderIds.includes(orderId)) {
        this.selectedOrderIds = this.selectedOrderIds.filter(id => id !== orderId)
      } else {
        this.selectedOrderIds = [...this.selectedOrderIds, orderId]
      }
    },
    toggleAllOrders(checked) {
      if (checked) {
        const currentIds = this.orders.map(order => order.id)
        this.selectedOrderIds = Array.from(new Set([...this.selectedOrderIds, ...currentIds]))
      } else {
        const currentIds = this.orders.map(order => order.id)
        this.selectedOrderIds = this.selectedOrderIds.filter(id => !currentIds.includes(id))
      }
    },
    isOrderRetrying(order) {
      return !!(order && this.retryingOrderIds.includes(order.id))
    },
    async resynchronizeSelected() {
      if (!this.selectedOrderIds.length) {
        this.showAlert('warning', 'Aucun colis sélectionné')
        return
      }
      await this.resynchronizeOrders(this.selectedOrderIds)
    },
    async resynchronizeOrders(orderIds) {
      const normalizedIds = orderIds.map(id => id.toString())
      this.retryLoading = true
      this.retryingOrderIds = Array.from(new Set([...this.retryingOrderIds, ...orderIds]))
      try {
        const { data } = await axios.post('/api/orders/resynchronize-bulk/', {
          order_ids: normalizedIds,
        })
        const results = data.results || {}
        const queued = (results.queued || []).length
        const skipped = (results.skipped_already_synchronized || []).length
        const noOutsourcing = (results.no_active_outsourcing || []).length
        const failed = (results.failed || []).length
        const notFound = (results.not_found || []).length
        const variant = failed || noOutsourcing ? 'warning' : 'success'

        this.showAlert(
          variant,
          `${queued} resynchronisation(s) lancée(s), ${skipped} déjà synchronisée(s), ${noOutsourcing} sans partenariat, ${failed} échec(s), ${notFound} introuvable(s).`,
        )
        this.selectedOrderIds = []
        await this.fetchFailedSyncOrders()
        this.startPolling()
      } catch (error) {
        this.showAlert('danger', 'Erreur lors de la resynchronisation')
      } finally {
        this.retryLoading = false
        this.retryingOrderIds = this.retryingOrderIds.filter(id => !orderIds.includes(id))
      }
    },
    startPolling() {
      this.stopPolling()
      this.pollCount = 0
      this.pollTimer = window.setInterval(async () => {
        this.pollCount += 1
        await this.fetchFailedSyncOrders()
        if (this.pollCount >= 3 || this.totalOrders === 0) {
          this.stopPolling()
        }
      }, 3000)
    },
    stopPolling() {
      if (this.pollTimer) {
        window.clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },
    previousPage() {
      if (this.currentPage <= 1) return
      this.currentPage -= 1
      this.fetchFailedSyncOrders()
    },
    nextPage() {
      if (this.currentPage >= this.totalPages) return
      this.currentPage += 1
      this.fetchFailedSyncOrders()
    },
    showAlert(variant, message) {
      this.alertVariant = variant
      this.alertMessage = message
      this.alertVisible = true
    },
  },
}
</script>

<style lang="scss" scoped>
.sync-state-page {
  padding: 2rem;
}
.sync-state-panel {
  padding: 1.5rem;
}
.sync-table {
  margin-top: 1rem;
}
.sync-metric {
  min-width: 110px;
  padding: 0.75rem 1rem;
  border: 1px solid #edf0f4;
  border-radius: 8px;
}
.sync-filter-select {
  width: 260px;
}
.retry-btn {
  min-width: 96px;
}
::v-deep .sync-select-col {
  width: 48px;
}
</style>
